/**
 * Auth store Zustand — miroir de pb.authStore
 *
 * Au lieu de gérer le token nous-mêmes, on s'abonne à pb.authStore.onChange
 * et on reflète l'état dans Zustand pour que les composants React réagissent.
 *
 * Le store est initialisé avec l'état courant de pb.authStore (hydratation depuis
 * localStorage via le SDK). Pas besoin de `persist` middleware Zustand — le SDK
 * s'en charge nativement avec la clé "pocketbase_auth".
 */

import { create } from "zustand";
import { pb } from "@/lib/pb";

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  /** Nom de fichier brut côté PocketBase (`users.avatar`). */
  avatar?: string;
  /** Board préféré pour ouverture automatique de la vue display. */
  preferred_display_board?: string | null;
  /** Cache-bust pour forcer le rechargement de l'image après un upload. */
  avatar_version?: number;
}

export interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  // Actions explicites (login/logout utilisés par les pages)
  login: (user: AuthUser, token: string) => void;
  logout: () => void;
  setUser: (user: AuthUser) => void;
  // Alias rétrocompatibilité
  setAuth: (user: AuthUser, token: string) => void;
  clearAuth: () => void;
}

/** Construit un AuthUser depuis le record pb.authStore.record */
function recordToUser(record: NonNullable<typeof pb.authStore.record>): AuthUser {
  return {
    id: record.id,
    email: (record["email"] as string | undefined) ?? "",
    name: (record["name"] as string | undefined) ?? "",
    avatar: (record["avatar"] as string | undefined) || undefined,
    preferred_display_board:
      (record["preferred_display_board"] as string | undefined) ?? null,
  };
}

/** Snapshot de l'état courant de pb.authStore */
function snapshot(): Pick<AuthState, "user" | "token" | "isAuthenticated"> {
  const record = pb.authStore.record;
  const token = pb.authStore.token || null;
  if (!pb.authStore.isValid || !record) {
    return { user: null, token: null, isAuthenticated: false };
  }
  return { user: recordToUser(record), token, isAuthenticated: true };
}

export const useAuthStore = create<AuthState>()((set) => {
  // S'abonner une fois aux changements du SDK — déclenche un re-render React
  pb.authStore.onChange(() => {
    set(snapshot());
  });

  return {
    // Initialisation avec l'état persisté par le SDK dans localStorage
    ...snapshot(),

    login: (user, token) => {
      // On laisse le SDK gérer le storage — cette action est appelée après
      // authWithPassword() dans les pages. Le onChange ci-dessus synchronise
      // automatiquement, mais on peut aussi forcer ici pour l'optimistic update.
      set({ user, token, isAuthenticated: true });
    },

    logout: () => {
      pb.authStore.clear(); // déclenche onChange → set({ user: null, ... })
    },

    setUser: (user) => set({ user }),

    // Alias rétrocompatibilité
    setAuth: (user, token) => set({ user, token, isAuthenticated: true }),
    clearAuth: () => {
      pb.authStore.clear();
    },
  };
});
